import { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  downloadInventoryControlRegistries,
  editReasonAndSolution,
  getInventoryControlRegisters,
  getMeasurementListJson,
  getPageableInventoryControlRegistriesByPeriod,
  reCalculateInventoryControl,
  RequestEditInventoryControlRegister,
  ResponseInventoryControlRegistry,
} from "api/generated";
import { useClient } from "hooks/useClient";
import { RequestGetInventoryControlDetails } from "api/generated";
import { message, notification, Progress, Statistic } from "antd";
import { isAxiosError } from "axios";
import moment from "moment";
import DistributionPointSelected from "context/MyDistributionPoint";
import { UsePaginationTableReturn } from "hooks/commons/usePaginationTable";
import { handleCsvDownload } from "common/helpers/generalUtils";

interface UseStockControlProps {
  idSubproduct?: number;
  startDate: string;
  endDate: string;
  fetchByDay: boolean;
  pagination: UsePaginationTableReturn<
    RequestGetInventoryControlDetails | ResponseInventoryControlRegistry
  >;
}

export const useStockControl = ({
  idSubproduct = 51,
  startDate,
  endDate,
  fetchByDay,
  pagination,
}: UseStockControlProps) => {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Referencias para los AbortController de cada petición
  const fetchStockControllerRef = useRef<AbortController | null>(null);
  const fetchStockByPeriodControllerRef = useRef<AbortController | null>(null);
  const downloadControllerRef = useRef<AbortController | null>(null);

  /**
   * Obtiene los registros de control de inventario por día
   */
  const fetchStockControl = useCallback(() => {
    if (!distributionPoint?.id || !fetchByDay) {
      return;
    }
    // Cancelar petición anterior si existe
    if (fetchStockControllerRef.current) {
      fetchStockControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    fetchStockControllerRef.current = controller;

    setLoading(true);
    setError(undefined);
    getInventoryControlRegisters({
      client,
      body: {
        requestInventoryControlDisplayPage: pagination.currentPage,
        requestInventoryControlDisplayAmount: pagination.pageSize,
        idSubproduct,
        requestInventoryControlStartDate: startDate,
        requestInventoryControlEndDate: endDate,
        distributionPointIds: [distributionPoint.id],
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        pagination.setItems(data?.details ?? []);
        pagination.setTotalItems(data?.inventoryControlTotalRecords ?? 0);
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          setError(`${err}`);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    client,
    idSubproduct,
    pagination.currentPage,
    pagination.pageSize,
    startDate,
    endDate,
    fetchByDay,
    distributionPoint,
    refreshTrigger,
  ]);

  /**
   * Obtiene los registros de control de inventario por período
   */
  const fetchStockControlByPeriod = useCallback(() => {
    if (!distributionPoint?.id || fetchByDay) {
      return;
    }

    // Cancelar petición anterior si existe
    if (fetchStockByPeriodControllerRef.current) {
      fetchStockByPeriodControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    fetchStockByPeriodControllerRef.current = controller;

    setLoading(true);
    setError(undefined);
    getPageableInventoryControlRegistriesByPeriod({
      client,
      query: {
        startDate,
        finalDate: endDate,
        distributionPoints: [distributionPoint.id],
        page: pagination.currentPage,
        size: pagination.pageSize,
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        pagination.setItems(data?.content ?? []);
        pagination.setTotalItems(data?.totalElements ?? 0);
      })
      .catch((err) => {
        console.error("Error fetching stock control:", err);
        setError(`${err}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    client,
    idSubproduct,
    pagination.currentPage,
    pagination.pageSize,
    startDate,
    endDate,
    fetchByDay,
    distributionPoint,
    refreshTrigger,
  ]);

  // Efecto para resetear la pagina cuando cambian el punto de distribucion
  useEffect(() => {
    pagination.setCurrentPage(0);
  }, [distributionPoint?.id]);

  // Efecto para cargar datos cuando cambian las dependencias
  useEffect(() => {
    if (!distributionPoint?.id) return;

    pagination.setItems([]);
    pagination.setTotalItems(0);

    if (fetchByDay) {
      fetchStockControl();
    } else {
      fetchStockControlByPeriod();
    }
  }, [
    distributionPoint?.id,
    startDate,
    endDate,
    pagination.currentPage,
    pagination.pageSize,
    fetchByDay,
    refreshTrigger,
  ]);

  /**
   * Recalcula el control de existencias (GIC) para una fecha específica
   * @param {moment.Moment} dateToReCalculate - Fecha para la cual se recalculará el inventario
   * @param {number} [desiredInitialVolume] - Volumen inicial deseado (opcional)
   * Muestra notificaciones de éxito/error
   */
  const recalculateGic = useCallback(
    (dateToReCalculate: moment.Moment, desiredInitialVolume?: number) => {
      if (!distributionPoint?.id) {
        return;
      }
      if (
        desiredInitialVolume !== undefined &&
        Number(desiredInitialVolume) <= 0
      ) {
        message.error("El volumen inicial debe de ser mayor a cero");
        return;
      }
      setLoading(true);
      const date = dateToReCalculate.format("YYYY-MM-DD");
      reCalculateInventoryControl({
        client,
        body: {
          startDate: date,
          reportType: "Regeneración de GIC",
          desiredInitialVolume,
          idSubproduct: 51,
          distributionPointId: distributionPoint.id,
        },
      })
        .then(() => {
          notification.success({
            message: `El calculo de existencias para el punto de distribución ${distributionPoint.name} 
          en la fecha ${date} 
          ha sido actualizado exitosamente`,
          });
        })
        .catch((err) => {
          let error = `${err}`;
          if (isAxiosError(err)) {
            error = err.response?.data.message;
          }
          message.error(error);
        })
        .finally(() => {
          setLoading(false);
          setRefreshTrigger((prev) => prev + 1);
        });
    },
    [distributionPoint, client, setRefreshTrigger],
  );

  /**
   * Recalcula el control de existencias (GIC) para un período completo (todos los días del mes)
   * @param {moment.Moment} periodDate - Fecha del período (mes) a recalcular
   */
  const recalculatePeriod = useCallback(
    async (periodDate: moment.Moment) => {
      if (!distributionPoint?.id) {
        return;
      }

      setLoading(true);
      const notificationKey = `recalculate-progress-key`;
      try {
        // Obtener todos los días del mes
        const startOfMonth = periodDate.clone().startOf("month");
        const endOfMonth = periodDate.clone().endOf("month");
        const currentDate = moment();
        let daysInMonth = endOfMonth.date();

        // Si es el mes y año actual, solo regenerar hasta el día actual
        const isCurrentMonth =
          periodDate.isSame(currentDate, "month") &&
          periodDate.isSame(currentDate, "year");

        if (isCurrentMonth) {
          daysInMonth = currentDate.date();
        }

        let successfulRecalculations = 0;
        let failedRecalculations = 0;

        // Procesar cada día del mes
        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = startOfMonth.clone().date(day);
          const dateStr = currentDate.format("YYYY-MM-DD");

          // Actualizar progreso
          notification.open({
            key: notificationKey,
            message: `Regenerando el período ${periodDate.format("YYYY-MM")}`,
            description: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Progress
                  type="circle"
                  percent={Number(((day * 100) / daysInMonth).toFixed(0))}
                  status={failedRecalculations === 0 ? "normal" : "exception"}
                />
                <Statistic
                  title="Ejecutando control de existencias"
                  value={day}
                  suffix={`/  ${daysInMonth}`}
                />
              </div>
            ),
            duration: 0,
          });

          try {
            await reCalculateInventoryControl({
              client,
              body: {
                startDate: dateStr,
                reportType: "Regeneración de GIC",
                idSubproduct: 51,
                distributionPointId: distributionPoint.id,
              },
            });

            successfulRecalculations++;
          } catch (err) {
            failedRecalculations++;
            console.error(`Error recalculating GIC for ${dateStr}:`, err);
          }
        }

        // Cerrar notificación de progreso
        notification.destroy(notificationKey);

        if (failedRecalculations > 0) {
          notification.warning({
            message: `No fue posible procesar la regeneración del período`,
            description: `No se procesaron los controles de existencia para: ${failedRecalculations} días.
          Visualiza los controles de existencia diarios para identificar el problema.
          `,
          });
        } else {
          notification.info({
            message: `Regeneración de período completada`,
            description: `Los controles de existencia para ${successfulRecalculations} días fueron recalculados`,
          });
        }

        // Recargar datos
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        notification.destroy(notificationKey);
        notification.error({
          message: `Error al procesar el período ${error}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [distributionPoint, client, setRefreshTrigger],
  );

  /**
   * Actualiza o inserta la causa y solución para una diferencia de inventario
   * @param {RequestEditInventoryControlRegister} request - Objeto con la información a actualizar
   * Muestra notificaciones de éxito/error
   */
  function upsertCauseAndSolutionOnDifference(
    request: RequestEditInventoryControlRegister,
  ) {
    setLoading(true);
    editReasonAndSolution({
      client,
      body: request,
    })
      .then(() => {
        notification.success({
          message: `Información de la inconsistencia guardada exitosamente`,
        });
      })
      .catch((err) => {
        let error = `${err}`;
        if (isAxiosError(err)) {
          error = err.response?.data.message;
        }
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
        setRefreshTrigger((prev) => prev + 1);
      });
  }

  /**
   * Obtiene la última medición para un control de existencias específico
   * @param {string} wareHouseInventoryControlId - ID del control de inventario
   * @returns {Promise<MeasurementTank>} Promesa que resuelve con un array de entradas de medición
   * @throws {Error} Lanza un error si la petición falla
   */
  async function getLastMeasurement(wareHouseInventoryControlId: string) {
    try {
      const data = await getMeasurementListJson({
        client,
        path: {
          wareHouseInventoryControlId,
        },
      });
      return data?.data?.entries ?? [];
    } catch (err) {
      let error = `${err}`;
      if (isAxiosError(err)) {
        error = err.response?.data.message;
      }
      message.error(error);
      throw new Error(error);
    }
  }

  /**
   * Descarga los registros de control de existencias
   */
  const downloadStockControl = useCallback(() => {
    if (!distributionPoint?.id) {
      return;
    }

    // Cancelar petición anterior si existe
    if (downloadControllerRef.current) {
      downloadControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    downloadControllerRef.current = controller;

    setLoading(true);
    setError(undefined);
    downloadInventoryControlRegistries({
      client,
      query: {
        startDate,
        finalDate: endDate,
        distributionPoints: [distributionPoint.id],
        byDay: fetchByDay,
      },
      signal: controller.signal,
    })
      .then(( data ) =>
        handleCsvDownload(data),
      )
      .catch((err) => {
        if (isAxiosError(err)) {
          setError(`${err}`);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    client,
    idSubproduct,
    pagination.currentPage,
    pagination.pageSize,
    startDate,
    endDate,
    fetchByDay,
    distributionPoint,
    refreshTrigger,
  ]);

  function reloadTables() {
    setRefreshTrigger((prev) => prev + 1);
  }

  return {
    // Estado
    loading,
    error,

    // Acciones
    recalculateGic,
    recalculatePeriod,
    upsertCauseAndSolutionOnDifference,
    getLastMeasurement,
    downloadStockControl,
    reloadTables,
  };
};
